"use client";

import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, Loader2 } from "lucide-react";
import { pdf } from "@react-pdf/renderer";
import type { InvoiceData } from "@/lib/invoice-types";
import InvoicePDF from "@/components/invoice-pdf";

function previewSrc(url: string): string {
  return `${url}#toolbar=0&navpanes=0&scrollbar=0&statusbar=0&messages=0`;
}

export default function PdfPreview({ data }: { data: InvoiceData }) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [nextPreviewUrl, setNextPreviewUrl] = useState<string | null>(null);
  const previewUrlRef = useRef<string | null>(null);
  const nextPreviewUrlRef = useRef<string | null>(null);
  const renderIdRef = useRef(0);

  useEffect(() => {
    let isActive = true;
    const nextRenderId = renderIdRef.current + 1;
    renderIdRef.current = nextRenderId;

    void pdf(<InvoicePDF data={data} />)
      .toBlob()
      .then((blob) => {
        if (!isActive || renderIdRef.current !== nextRenderId) return;

        const generatedUrl = URL.createObjectURL(blob);
        const previousNextUrl = nextPreviewUrlRef.current;
        nextPreviewUrlRef.current = generatedUrl;
        setNextPreviewUrl(generatedUrl);
        if (previousNextUrl) {
          URL.revokeObjectURL(previousNextUrl);
        }
      });

    return () => {
      isActive = false;
    };
  }, [data]);

  useEffect(() => {
    return () => {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
      }
      if (nextPreviewUrlRef.current) {
        URL.revokeObjectURL(nextPreviewUrlRef.current);
      }
    };
  }, []);

  const handleNextPreviewLoaded = () => {
    const loadedUrl = nextPreviewUrlRef.current;
    if (!loadedUrl) return;

    const previousActiveUrl = previewUrlRef.current;
    previewUrlRef.current = loadedUrl;
    setPreviewUrl(loadedUrl);

    nextPreviewUrlRef.current = null;
    setNextPreviewUrl(null);

    if (previousActiveUrl && previousActiveUrl !== loadedUrl) {
      URL.revokeObjectURL(previousActiveUrl);
    }
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const blob = await pdf(<InvoicePDF data={data} />).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${data.invoiceNumber || "faktura"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="relative flex-1 rounded-lg border bg-muted/30 overflow-hidden min-h-0">
        {previewUrl ? (
          <iframe title="PDF preview" src={previewSrc(previewUrl)} className="size-full border-0" />
        ) : (
          <div className="flex size-full items-center justify-center text-xs text-muted-foreground">
            Genererar förhandsgranskning...
          </div>
        )}
        {nextPreviewUrl && nextPreviewUrl !== previewUrl ? (
          <iframe
            title="PDF preview preloader"
            src={previewSrc(nextPreviewUrl)}
            className="absolute inset-0 size-full border-0 opacity-0 pointer-events-none"
            onLoad={handleNextPreviewLoaded}
          />
        ) : null}
      </div>
    </div>
  );
}
